import { useState } from 'react'
import { EditExternalAssetModal } from '@/components/externalAssets/EditExternalAssetModal'
import { formatNumberDe } from '@/lib/format'
import { useDeleteExternalAsset, useExternalAssets } from '@/lib/useExternalAssets'
import type { AssetClass, ExternalAsset, UpdateFrequency } from '@/types/externalAsset'

const ASSET_CLASS_LABELS: Record<AssetClass, string> = {
  crypto: 'Crypto',
  real_estate: 'Immobilie',
  commodity_physical: 'Commodity',
  cash: 'Cash',
  other: 'Sonstiges',
}

const UPDATE_FREQUENCY_LABELS: Record<UpdateFrequency, string> = {
  monthly: 'monatlich',
  quarterly: 'quartalsweise',
  on_demand: 'on demand',
}

const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return DATE_FORMATTER.format(new Date(iso))
}

function isOverdue(iso: string | null): boolean {
  if (!iso) return false
  const due = new Date(iso)
  if (Number.isNaN(due.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return due < today
}

interface ExternalAssetListProps {
  /** Erweiterte Anzeige mit Class, Update-Frequenz, last_updated, next_update_due. */
  detailed?: boolean
}

export function ExternalAssetList({ detailed = false }: ExternalAssetListProps) {
  const assetsQuery = useExternalAssets()
  const deleteAsset = useDeleteExternalAsset()
  const [editingAsset, setEditingAsset] = useState<ExternalAsset | null>(null)

  const assets = assetsQuery.data ?? []

  if (assetsQuery.isLoading) {
    return <p className="font-mono text-sm text-muted">Lade Assets…</p>
  }

  if (assets.length === 0) {
    return <p className="font-mono text-sm text-faint">Keine External Assets angelegt.</p>
  }

  const handleDelete = (id: number, name: string) => {
    if (window.confirm(`Asset "${name}" wirklich löschen?`)) {
      deleteAsset.mutate(id)
    }
  }

  return (
    <>
      <ul className="flex flex-col">
        {assets.map((a) => {
          const overdue = isOverdue(a.next_update_due)
          return (
            <li
              key={a.id}
              className="hairline-b flex flex-col gap-1 py-3 font-mono text-sm md:flex-row md:items-baseline md:justify-between"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-ink">{a.name}</span>
                  {overdue && (
                    <span
                      className="tracked-caps border border-loss/40 bg-loss/10 px-1.5 py-0.5 text-[9px] text-loss"
                      title={`Update fällig seit ${formatDate(a.next_update_due)}`}
                    >
                      Update fällig
                    </span>
                  )}
                </div>
                {detailed && (
                  <span className="block break-words text-[11px] text-faint">
                    {ASSET_CLASS_LABELS[a.asset_class] ?? a.asset_class} ·{' '}
                    {UPDATE_FREQUENCY_LABELS[a.update_frequency] ?? a.update_frequency} · zuletzt
                    aktualisiert {formatDate(a.last_updated)}
                    {a.next_update_due && ` · fällig ${formatDate(a.next_update_due)}`}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-baseline gap-4">
                <span className="text-ink">{formatNumberDe(a.current_value_eur, 2)} €</span>
                <button
                  type="button"
                  onClick={() => setEditingAsset(a)}
                  className="font-mono text-xs text-muted transition-colors hover:text-bernstein"
                >
                  Bearbeiten
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(a.id, a.name)}
                  disabled={deleteAsset.isPending}
                  className="font-mono text-xs text-muted transition-colors hover:text-loss disabled:opacity-50"
                >
                  Löschen
                </button>
              </div>
            </li>
          )
        })}
      </ul>
      <EditExternalAssetModal
        open={editingAsset !== null}
        asset={editingAsset}
        onClose={() => setEditingAsset(null)}
      />
    </>
  )
}
